import { Box, Typography, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReactECharts from 'echarts-for-react';
import LoaderComponent from 'components/Loader';
import { useMemo } from 'react';
import { LongArrowUp, LongArrowDown, InfoIcon } from '../icons';

export interface TrendSeries {
  name: string;
  data: { date: string; count: number }[];
}

export interface StatCardData {
  label: string;
  value: string;
  subtitle?: string;
  trend?: 'up' | 'down' | 'neutral';
}

export interface TrendStats {
  totalAlerts: number;
  peakDay: string;
  avgPerDay?: number | string;
  avgResolution?: string;
  topSector?: string;
  topProgram?: string;
  totalSubtitle?: string;
  totalTrend?: 'up' | 'down' | 'neutral';
  peakDaySubtitle?: string;
  avgSubtitle?: string;
  avgTrend?: 'up' | 'down' | 'neutral';
  topSubtitle?: string;
  [key: string]: any;
}

export interface StatCardConfig {
  label: string;
  value: string;
  subtitle?: string;
  trend?: 'up' | 'down' | 'neutral';
  tooltip?: string;
  arrowOnly?: boolean;
}

interface TrendLineChartProps {
  title: string;
  series: TrendSeries[];
  stats: TrendStats;
  isLoading: boolean;
  topLabel: string;
  topValue: string;
  cards?: StatCardConfig[];
}

const TrendLineChart = ({ title, series, stats, isLoading, topLabel, topValue, cards }: TrendLineChartProps) => {
  const theme = useTheme();
  const wc = theme.palette.widget?.colors ?? {};
  const chartColors = ([wc.color1, wc.color2, wc.color3, wc.color4, wc.color5].filter(Boolean) as string[]).length > 0
    ? [wc.color1, wc.color2, wc.color3, wc.color4, wc.color5].filter(Boolean) as string[]
    : ['#00B4D8', '#43A047', '#FB8C00', '#E53935', '#7B1FA2'];

  const chartOption = useMemo(() => {
    const dates = series[0]?.data.map((d) => d.date) ?? [];

    return {
      tooltip: { trigger: 'axis' },
      legend: {
        data: series.map((s) => s.name),
        bottom: 0,
        type: 'scroll',
        textStyle: { fontSize: 15, padding: [0, 0, 0, 4], fontFamily: 'Montserrat' },
        icon: 'path://M2,2 L10,2 Q12,2 12,4 L12,12 Q12,14 10,14 L2,14 Q0,14 0,12 L0,4 Q0,2 2,2 Z',
        itemWidth: 16,
        itemHeight: 16,
      },
      grid: { left: '8%', right: '4%', top: '10%', bottom: '18%', containLabel: true },
      xAxis: {
        type: 'category',
        data: dates,
        axisLabel: {
          formatter: (val: string) => {
            const d = new Date(val);
            if (isNaN(d.getTime())) return val;
            return `${d.getDate()}/${d.getMonth() + 1}`;
          },
          rotate: 45,
          fontSize: 10,
          hideOverlap: true,
        },
      },
      yAxis: { type: 'value' },
      series: series.map((s, i) => ({
        name: s.name,
        type: 'line',
        data: s.data.map((d) => d.count),
        smooth: false,
        lineStyle: { width: 2 },
        itemStyle: { color: chartColors[i % chartColors.length] },
      })),
    };
  }, [series, chartColors]);

  if (isLoading) return <LoaderComponent />;

  if (!stats || series.length === 0) {
    return <Box sx={{ p: 2, textAlign: 'center' }}>No data available</Box>;
  }

  return (
    <Box sx={{ width: { xs: '100%', md: '66.67%' } }}>
      {title && (
        <Typography variant="h4" sx={{ fontWeight: 500, mb: 2, color: 'primary.main' }}>{title}</Typography>
      )}
      <ReactECharts
        option={chartOption}
        style={{ height: 350, width: '100%' }}
        opts={{ renderer: 'canvas' }}
        notMerge
      />
      <Box sx={{ display: 'flex', gap: 3, mt: 4, flexWrap: 'wrap' }}>
        {cards?.map((card) => (
          <StatCard key={card.label} {...card} />
        ))}
      </Box>
    </Box>
  );
};


const StatCard = ({ label, value, subtitle, trend, tooltip, arrowOnly }: StatCardConfig) => {
  const theme = useTheme();
  const upColor = theme.palette.success.main;
  const downColor = '#E53935';
  const trendColor =
  trend === 'up'
    ? upColor
    : trend === 'down'
    ? downColor
    : theme.palette.text.subtle;

  return (
    <Box sx={{ p: 2, borderRadius: 1, backgroundColor: theme.palette.kpiTile.background.card, width: 202, height: 145, minWidth: 0, overflow: 'hidden', flexShrink: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Typography sx={{ flex: 1, fontSize: '1.25rem', fontWeight: 500, color: theme.palette.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{label}</Typography>
        {tooltip && (
          <Tooltip title={tooltip} placement="top">
            <Box component="span" sx={{ cursor: 'help', flexShrink: 0, lineHeight: 0, display: 'inline-flex' }}>
              <InfoIcon size={26} color={theme.palette.info.main} />
            </Box>
          </Tooltip>
        )}
      </Box>
      {arrowOnly ? (
        <Box sx={{ display: 'flex', alignItems: 'center', py: 1, mb: subtitle ? 0.75 : 0 }}>
          {trend === 'up' && <LongArrowUp color={upColor} size={43} />}
          {trend === 'down' && <LongArrowDown color={downColor} size={43} />}
        </Box>
      ) : (
        <Typography sx={{ fontSize: '3rem', fontWeight: 400, color: theme.palette.kpiTile.text.value, lineHeight: 1.1, mb: subtitle ? 0.75 : 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</Typography>
      )}
      {subtitle && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
          {!arrowOnly && trend === 'up' && <LongArrowUp color={upColor} size={28} />}
          {!arrowOnly && trend === 'down' && <LongArrowDown color={downColor} size={28} />}
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.2, color: trendColor, overflow: 'hidden' }}>
            {subtitle}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default TrendLineChart;
